import { useQuery } from '@tanstack/react-query';

import { useAccessToken } from '@hooks/useAccessToken';
import { fetchPlayers, type Player } from '@api-service/players';

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

export type EnrichedPlayer = Pick<
  Player,
  | 'id'
  | 'displayName'
  | 'imageUrl'
  | 'kitImageUrl'
  | 'ownership'
  | 'priceChange'
  | 'team'
>;

const ENRICHMENT_BATCH_LIMIT = 50;

/**
 * Batched fetch of player enrichment data (image, ownership, price change)
 * for the players returned by the transfer recommendation endpoint. One
 * request resolves the whole transfer set; results are keyed by UUID.
 */
export const usePlayerEnrichment = (playerIds: string[]) => {
  const { accessToken, accessTokenIsFetching } = useAccessToken();

  const uniqueIds = [...new Set(playerIds.filter(Boolean))];
  const idsKey = uniqueIds.sort().join(',');

  const { data } = useQuery({
    queryKey: ['playerEnrichment', idsKey],
    queryFn: () =>
      fetchPlayers({
        ...(accessToken ? { accessToken } : {}),
        ids: idsKey,
        include: 'priceChange',
        limit: ENRICHMENT_BATCH_LIMIT
      }),
    enabled: uniqueIds.length > 0 && !accessTokenIsFetching,
    staleTime: FIVE_MINUTES_IN_MS,
    refetchOnWindowFocus: false
  });

  const map = new Map<string, EnrichedPlayer>();
  for (const player of data?.data ?? []) {
    map.set(player.id, {
      id: player.id,
      displayName: player.displayName,
      imageUrl: player.imageUrl,
      kitImageUrl: player.kitImageUrl,
      ownership: player.ownership,
      priceChange: player.priceChange,
      team: player.team
    });
  }

  return {
    enrichmentById: map,
    getEnrichment: (id: string): EnrichedPlayer | undefined => map.get(id)
  };
};
